import React, { useEffect, useCallback, useRef, useState } from 'react';
import { Form } from '@unform/web';
import { useNavigate, useParams } from 'react-router-dom';
import { FiLock } from 'react-icons/fi';
import * as Yup from 'yup';

import loadingGif from '~/assets/img/loadingMusic.gif';
import { useToast } from '~/hooks/toast';
import Input from '~/components/Input';
import Button from '~/components/Button';
import getValidationErrors from '~/utils/getValidationErrors';

import { BackgroundSectionForgotPass, DivForgotPass } from './styles';
import { useChangeForgotPasswordByTokenMutation, useVerifyForgotPasswordTokenMutation } from 'src/graphql/generated/graphql';

const NewPass = () => {
  const { hash } = useParams();

  const formRef = useRef(null);
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [disable, setDisable] = useState('');
  const [loading, setLoading] = useState(false);

  const [verifyForgotPasswordToken] = useVerifyForgotPasswordTokenMutation();
  const [changeForgotPassword] = useChangeForgotPasswordByTokenMutation();

  useEffect(() => {
    if (hash) {
      verifyForgotPasswordToken({
        variables: {
          data: {
            tokenId: hash,
          },
        },
      })
        .then(response => {
          const message = response.data?.verifyForgotPasswordToken;
          addToast({
            type: 'success',
            title: `${message}`,
            description: `Insira uma nova senha`,
          });
        })
        .catch(error => {
          let errorMessage = 'Erro ao confirmar token';

          if (error.graphQLErrors && error.graphQLErrors.length > 0) {
            errorMessage = error.graphQLErrors[0].message;
          } else if (error.networkError) {
            errorMessage = 'Serviço indisponível no momento';
          }

          addToast({
            type: 'error',
            title: errorMessage,
            description: 'Tente novamente enviando uma nova solicitação ao seu e-mail.',
          });
        });
    }
  }, [addToast, hash, navigate, verifyForgotPasswordToken]);

  const handleSubmit = useCallback(
    async data => {
      try {
        formRef.current.setErrors({});
        const schema = Yup.object().shape({
          password: Yup.string()
            .required('Insira uma nova senha')
            .min(6, 'No mínimo 6 dígitos'),
          password_confirmation: Yup.string()
            .required('Insira uma senha')
            .oneOf([Yup.ref('password'), null], 'As senhas devem corresponder'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        setDisable(disable === '' ? 'active' : '');

        setLoading(true);

        // const response = await api.put('/auth/change/forgot/password', {
        //   token: hash,
        //   password: data.password,
        //   password_confirmation: data.password_confirmation,
        // });

        const response = await changeForgotPassword({
          variables: {
            data: {
              token: hash,
              password: data.password,
            },
          },
        });

        const message = response.data?.changeForgotPasswordByToken;

        setLoading(false);

        navigate(`/login/senha-alterada/${message}`);

        addToast({
          type: 'success',
          title: 'Senha alterada!',
          description: `${message}`,
        });
      } catch (error) {
        setLoading(false);
        setDisable(disable === 'active' ? '' : '');

        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);

          formRef.current.setErrors(errors);
          return;
        }

        let errorMessage = 'Erro ao alterar senha';

        if (error.graphQLErrors && error.graphQLErrors.length > 0) {
          errorMessage = error.graphQLErrors[0].message;
        } else if (error.networkError) {
          errorMessage = 'Serviço indisponível no momento';
        }

        addToast({
          type: 'error',
          title: errorMessage,
          description: 'Tente novamente.',
        });
      }
    },
    [addToast, hash, navigate, disable, changeForgotPassword]
  );

  return (
    <BackgroundSectionForgotPass>
      <DivForgotPass className="cont">
        <h1>Criar uma nova senha</h1>
        <Form ref={formRef} onSubmit={handleSubmit}>
          <Input
            type="password"
            name="password"
            label="Nova senha:"
            icon={FiLock}
          />
          <Input
            type="password"
            name="password_confirmation"
            label="Confirme a nova senha:"
            icon={FiLock}
          />
          <Button type="submit" disabled={loading} className={disable}>
            Alterar senha
          </Button>
          {loading && <img src={loadingGif} alt="Carregando..." width="50px" />}
        </Form>
      </DivForgotPass>
    </BackgroundSectionForgotPass>
  );
};

export default NewPass;
